import { useAction, useMutation } from "convex/react"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAppAuth } from "@/hooks/useAppAuth"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useSolanaSign } from "@/hooks/useSolanaSign"
import { useWebBalance } from "@/hooks/useWebBalance"
import { getErrorMessage, isQuotaExhausted } from "@/lib/errors"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import { MICRO_USDC } from "~shared/constants"

interface EngagementBarProps {
	captchaUsd: number
	cashtag?: string | null
	liked: boolean
	likeCount: number
	onCashtagBuy?: (ticker: string) => void
	onQuote?: () => void
	onReply?: () => void
	onInstantBuy?: () => void
	postId: Id<"posts">
	replyCount: number
	repostCount: number
	reposted: boolean
}

export const EngagementBar = memo(function EngagementBar({
	captchaUsd,
	cashtag,
	liked,
	likeCount,
	onCashtagBuy,
	onQuote,
	onReply,
	onInstantBuy,
	postId,
	replyCount,
	repostCount,
	reposted,
}: EngagementBarProps) {
	const { isAuthenticated } = useAppAuth()
	const navigate = useNavigate()
	const { signTransaction } = useSolanaSign()
	const { usdcBalance, refetchUSDC } = useWebBalance()
	const balanceRef = useRef(usdcBalance)
	balanceRef.current = usdcBalance
	const toggleLike = useMutation(api.posts.toggleLike)
	const cancelRepost = useMutation(api.posts.cancelRepost)
	const freeCreateRepost = useMutation(api.freeActions.freeCreateRepost)
	const prepareRepost = useAction(api.swapPrepare.prepareRepost)
	const commitPost = useAction(api.swapPrepare.commitPost)
	const [heartPop, setHeartPop] = useState(false)
	const [reposting, setReposting] = useState(false)
	const [showRepostMenu, setShowRepostMenu] = useState(false)
	const repostMenuRef = useRef<HTMLDivElement>(null)

	// Optimistic like state — the feed's page cache only subscribes to the
	// last loaded cursor, so earlier pages never receive reactive updates.
	// Track a local override that is cleared once the server prop catches up.
	const [optimisticLike, setOptimisticLike] = useState<{
		liked: boolean
		count: number
	} | null>(null)

	const displayLiked = optimisticLike?.liked ?? liked
	const displayLikeCount = optimisticLike?.count ?? likeCount

	// Clear optimistic state once the server prop reflects the change
	const prevServerLiked = useRef(liked)
	useEffect(() => {
		if (liked !== prevServerLiked.current) {
			prevServerLiked.current = liked
			setOptimisticLike(null)
		}
	}, [liked])

	// Heart pop animation on like (from either server or optimistic update)
	const prevDisplayLiked = useRef(displayLiked)
	useEffect(() => {
		if (displayLiked && !prevDisplayLiked.current) {
			setHeartPop(true)
			const timer = setTimeout(() => setHeartPop(false), 350)
			return () => clearTimeout(timer)
		}
		prevDisplayLiked.current = displayLiked
	}, [displayLiked])

	const handleLike = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (!isAuthenticated) {
				Promise.resolve(navigate("/login")).catch(() => {})
				return
			}
			const newLiked = !displayLiked
			setOptimisticLike({
				liked: newLiked,
				count: newLiked ? likeCount + 1 : Math.max(0, likeCount - 1),
			})
			toggleLike({ post_id: postId }).catch(() => {
				setOptimisticLike(null)
				toast.error("Failed to like post")
			})
		},
		[isAuthenticated, navigate, toggleLike, postId, displayLiked, likeCount],
	)

	const handleReply = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (onReply) {
				onReply()
			} else {
				Promise.resolve(navigate(`/post/${postId}?reply=true`)).catch(() => {})
			}
		},
		[navigate, onReply, postId],
	)

	const closeRepostMenu = useCallback(() => setShowRepostMenu(false), [])
	useClickOutside(repostMenuRef, showRepostMenu, closeRepostMenu)

	const doRepost = useCallback(async () => {
		if (balanceRef.current < MICRO_USDC) {
			toast.error("Insufficient balance — add funds to repost", {
				action: {
					label: "Add funds",
					onClick: () => {
						Promise.resolve(navigate("/wallet")).catch(() => {})
					},
				},
			})
			return
		}

		setReposting(true)
		try {
			const prepared = await prepareRepost({ post_id: postId })
			const signedSwapTx = await signTransaction(prepared.swapTx)
			const signedFeeTx = prepared.feeTx ? await signTransaction(prepared.feeTx) : undefined
			await commitPost({
				content: "",
				type: "repost",
				repost_of: postId,
				prepare_token: prepared.prepareToken,
				signed_swap_tx: signedSwapTx,
				signed_fee_tx: signedFeeTx,
			})
			toast.success("Reposted!")
			refetchUSDC()
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to repost")
		} finally {
			setReposting(false)
		}
	}, [navigate, prepareRepost, commitPost, signTransaction, refetchUSDC, postId])

	const handleRepost = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (!isAuthenticated) {
				Promise.resolve(navigate("/login")).catch(() => {})
				return
			}
			if (reposting) {
				return
			}

			if (reposted) {
				setReposting(true)
				cancelRepost({ post_id: postId })
					.then(() => toast.success("Repost removed"))
					.catch(() => toast.error("Failed to remove repost"))
					.finally(() => setReposting(false))
				return
			}

			// Try free repost first, fall back to paid menu
			setReposting(true)
			try {
				await freeCreateRepost({ post_id: postId })
				toast.success("Reposted!")
			} catch (e: unknown) {
				if (isQuotaExhausted(e)) {
					setShowRepostMenu(true)
				} else {
					const msg = getErrorMessage(e)
					toast.error(msg || "Failed to repost")
				}
			} finally {
				setReposting(false)
			}
		},
		[isAuthenticated, navigate, reposting, reposted, cancelRepost, postId, freeCreateRepost],
	)

	return (
		<div className="mt-2 flex items-center gap-4 text-app-text-secondary text-sm sm:gap-6">
			<button
				aria-label={`Reply${replyCount > 0 ? ` (${replyCount})` : ""}`}
				className="-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 transition-colors transition-interact hover:text-app-mention active:scale-95"
				onClick={handleReply}
				type="button"
			>
				<svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
					/>
				</svg>
				{replyCount > 0 && <span>{replyCount}</span>}
			</button>
			<div className="relative" ref={repostMenuRef}>
				<button
					aria-label={`Repost${repostCount > 0 ? ` (${repostCount})` : ""}`}
					className={`-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 transition-colors transition-interact active:scale-95 ${reposted ? "text-app-green" : "hover:text-app-green"} ${reposting ? "opacity-50" : ""}`}
					disabled={reposting}
					onClick={handleRepost}
					type="button"
				>
					<svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path
							d="M17 1l4 4-4 4"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
						/>
						<path
							d="M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
						/>
						<path
							d="M21 13v2a4 4 0 0 1-4 4H3"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
						/>
					</svg>
					{repostCount > 0 && <span>{repostCount}</span>}
				</button>
				{showRepostMenu && (
					<div className="absolute bottom-full left-1/2 z-50 mb-2 flex w-48 -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-app-border bg-app-surface shadow-xl">
						<button
							className="px-4 py-3 text-center font-medium text-app-text text-sm transition-colors hover:bg-app-surface-light"
							onClick={e => {
								e.stopPropagation()
								e.preventDefault()
								setShowRepostMenu(false)
								doRepost().catch(() => {})
							}}
							type="button"
						>
							Repost
						</button>
						<div className="mx-3 border-app-border border-t" />
						<button
							className="px-4 py-3 text-center font-medium text-app-text text-sm transition-colors hover:bg-app-surface-light"
							onClick={e => {
								e.stopPropagation()
								e.preventDefault()
								setShowRepostMenu(false)
								onQuote?.()
							}}
							type="button"
						>
							Quote
						</button>
					</div>
				)}
			</div>
			<button
				aria-label={
					displayLiked
						? `Unlike (${displayLikeCount})`
						: `Like${displayLikeCount > 0 ? ` (${displayLikeCount})` : ""}`
				}
				className={`-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 transition-colors transition-interact active:scale-95 ${displayLiked ? "text-app-red" : "hover:text-app-red"}`}
				onClick={handleLike}
				type="button"
			>
				<svg
					className={`h-[18px] w-[18px] ${heartPop ? "animate-heart-pop" : ""}`}
					fill={displayLiked ? "currentColor" : "none"}
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
					/>
				</svg>
				{displayLikeCount > 0 && <span>{displayLikeCount}</span>}
			</button>
			{cashtag ? (
				<button
					className="-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 font-semibold transition-colors transition-interact hover:text-app-text active:scale-95"
					onClick={e => {
						e.stopPropagation()
						e.preventDefault()
						onCashtagBuy?.(cashtag)
					}}
					type="button"
				>
					${cashtag}
				</button>
			) : (
				<button
					className="-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 font-semibold transition-colors transition-interact hover:text-app-text active:scale-95"
					onClick={e => {
						e.stopPropagation()
						e.preventDefault()
						toast("Every post auto-buys $1 of $CAPTCHA", {
							icon: <img alt="" className="h-4 w-4 rounded" src="/newicon.png" />,
						})
					}}
					type="button"
				>
					${captchaUsd}
				</button>
			)}
			{onInstantBuy && (
				<button
					aria-label="Instant buy $10"
					className="-m-1.5 flex items-center gap-1.5 rounded-lg p-1.5 text-app-green transition-colors transition-interact hover:text-app-green/80 active:scale-95"
					onClick={e => {
						e.stopPropagation()
						e.preventDefault()
						onInstantBuy()
					}}
					type="button"
				>
					<svg className="h-[18px] w-[18px]" fill="currentColor" viewBox="0 0 24 24">
						<path d="M13 3 4 14h7l-1 8 9-11h-7l1-8z" />
					</svg>
				</button>
			)}
			<button
				aria-label="Share post"
				className="-m-1.5 ml-auto flex items-center gap-1.5 rounded-lg p-1.5 transition-colors transition-interact hover:text-app-text active:scale-95"
				onClick={e => {
					e.stopPropagation()
					e.preventDefault()
					const url = `${window.location.origin}/post/${postId}`
					if (navigator.share) {
						navigator.share({ url, title: "Captcha" }).catch((err: unknown) => {
							// Ignore user cancellation, log real failures
							if (err instanceof Error && err.name !== "AbortError") {
								toast.error("Share failed")
							}
						})
					} else {
						navigator.clipboard
							.writeText(url)
							.then(() => toast.success("Link copied"))
							.catch(() => toast.error("Failed to copy link"))
					}
				}}
				type="button"
			>
				<svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
					/>
				</svg>
			</button>
		</div>
	)
})
